import { Sites } from '@ps/platform-message-framework/common/Sites';
import { RegulatorAmlValEventType } from '@ps/platform-message-framework/regulator/RegulatorAmlValEventType';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { CashierPath } from '@ps/web-pam-types';
import * as Sentry from '@sentry/react';
import { isEmpty } from 'lodash';
import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import type { Store } from '@swf/core/store';
import { getState, select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';

import useHasMitIdDataContext from '../../hooks/useHasMitIdDataContext';
import useMitIdDataContext from '../../hooks/useMitIdDataContext';
import type {
  DepositPayload,
  EnvoyBankInfo,
  FirstWizardData,
  PaySystemDetails,
  PsPayBank,
  SavedCards
} from '../../types/Cashier';
import { CashierFlowType } from '../../types/Cashier';
import { CardType } from '../../types/cardType';
import { dollarsToCents, getSavedMethod, trimWithoutRounding } from '../../utils/cashier';
import { isDenmarkCitizen } from '../RMF/utils/rmf';

import { noInput, noInputSaved } from './data';

const useDepositGuardsData = () => {
  const store = useStore() as Store;
  const locale = useSelector(select.locale.get);
  const site = useSelector(select.cms.getSiteId);
  const pathname = useSelector(select.location.getPathname);
  const query = useSelector(select.location.getQuery);
  const isReloader = useSelector(select.account.isReloader);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const amlEventType = useSelector(select.account.getAmlEventType);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const currentlyTakingBreak = isSelfExcluded || nationalExclusion;
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const isPromoBanCoolOffPeriodActive = useSelector(select.account.isPromoBanCoolOffPeriodActive);
  const isSuspendedRM = useSelector(select.account.isSuspendRM);
  const occupationInfo = useSelector(select.account.getOccupationInfo);
  const isRealMoneyOK = useSelector(select.account.isRealMoneyOK);
  const isSiteDE = site === Sites.DE;
  const isSiteES = site === Sites.SPA;
  const isSiteDK = site === Sites.DEN;
  const isSiteITA = site === Sites.ITA;
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const isSiteUSPA = useSelector(select.cms.isSiteUSPA);
  const hasMitIdData = useMitIdDataContext(CashierPath.Deposit);
  const hasDepositMitIdDataContext = useHasMitIdDataContext();
  const isSWCEmailVerificationEnabled = useFeature(FeatureFlag.SwcEmailVerification);
  const emailVerified = useSelector(select.account.getEmailValidationFlags)?.verified;

  const depositNoDetails = ({
    values: { deposit },
    data: { envoyBanks, savedCards, savedCardInfoId, paySystemDetails, asPaybankInfo }
  }: {
    values: { deposit: DepositPayload };
    data: {
      envoyBanks: EnvoyBankInfo[];
      savedCards: SavedCards;
      savedCardInfoId: number;
      paySystemDetails: PaySystemDetails;
      asPaybankInfo: PsPayBank[];
    };
  }) => {
    const { type } = deposit;

    if (savedCards?.length && savedCardInfoId) {
      const [{ savedCard }] = getSavedMethod(savedCards, savedCardInfoId);
      if (savedCard && noInputSaved.includes(savedCard.paySystem)) {
        return true;
      }
    }
    const hasDefaultAsPaybankMethod = asPaybankInfo?.some((item) => item.default);

    return (
      hasDefaultAsPaybankMethod ||
      envoyBanks.length === 1 ||
      (type === CardType.PayPal && !paySystemDetails.info.supportBillingAgreement) ||
      noInput.includes(type)
    );
  };

  const depositMitIdDataPresent = ({ data: { rmfPassed } }: WizardContext) =>
    !rmfPassed && hasMitIdData;

  const hasDepositMitIdData = ({ data }: WizardContext) => hasDepositMitIdDataContext({ data });
  const depositResultContextPresent = useCallback(
    () =>
      pathname.startsWith(CashierPath.DepositResult) ||
      (pathname.startsWith(`/${locale}${CashierPath.DepositResult}`) && !!query),
    [locale, pathname, query]
  );

  const checkSWFFlowGuard = ({
    data: { cashierFlow }
  }: {
    data: { cashierFlow: CashierFlowType };
  }) => cashierFlow === CashierFlowType.FirstDeposit || depositResultContextPresent();

  const depositDetailsConfirmCheck = ({
    data: { savedCardInfoId },
    values: { deposit }
  }: {
    data: { savedCardInfoId: number };
    values: { deposit: DepositPayload };
  }) => {
    const { type } = deposit;
    const isSWCECheckConfirmationEnabled = select.feature.check(
      getState(),
      FeatureFlag.SwcECheckConfirmation
    );
    return isSWCECheckConfirmationEnabled && type === CardType.ECheck && !savedCardInfoId;
  };

  const shouldProcessBonus = ({ values }: { values: { deposit: { bonusCode?: string } } }) =>
    !!values.deposit?.bonusCode;

  const shouldProcessAmountBonus = shouldProcessBonus;
  const shouldProcessOfferBonus = shouldProcessBonus;

  const showWelcomeBonusOffer = useCallback(
    ({ data: { crmBonusCode } }: { data: { crmBonusCode: string } }): boolean => {
      const isSWCWelcomeBonusScreenEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcWelcomeBonusScreen
      );
      const isSWCAffiliateBonusEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcAffiliateBonus
      );
      const isSwcRmfBonusScreenEnabled = select.feature.check(
        getState(),
        FeatureFlag.SwcRmfBonusScreen
      );
      const showWelcomeBonus =
        !!isSWCWelcomeBonusScreenEnabled &&
        !!(isSWCAffiliateBonusEnabled || crmBonusCode) &&
        !isReloader &&
        !hideBonusCode;

      if (isSiteES) {
        return (
          ((isSwcRmfBonusScreenEnabled && !isReloader) || !isSwcRmfBonusScreenEnabled) &&
          showWelcomeBonus &&
          !currentlyTakingBreak &&
          !isPromoBanCoolOffPeriodActive
        );
      } else if (isSiteDE) {
        const isDeOasisBonusEligibilityAllowed = select.account.isDeOasisBonusEligibilityAllowed(
          store.getState()
        );
        const showBonus =
          isDeOasisBonusEligibilityAllowed &&
          !isReloader &&
          isSWCWelcomeBonusScreenEnabled &&
          !!(isSWCAffiliateBonusEnabled || crmBonusCode);

        return showBonus;
      }
      if (!showWelcomeBonus && isSiteITA) {
        Sentry.captureException(new Error('Cashier - Bonus not shown in IT site'), {
          extra: {
            isSWCWelcomeBonusScreenEnabled,
            isSWCAffiliateBonusEnabled,
            crmBonusCode,
            isReloader,
            isMobileEmbedded,
            hideBonusCode
          }
        });
      }
      return showWelcomeBonus;
    },
    [
      currentlyTakingBreak,
      hideBonusCode,
      isMobileEmbedded,
      isPromoBanCoolOffPeriodActive,
      isReloader,
      isSiteDE,
      isSiteES,
      isSiteITA,
      store
    ]
  );

  const accountSuspended = useCallback(
    ({ data: { depositRestricted } }: { data: { depositRestricted: boolean } }) => {
      const rootState = getState();
      const isRealMoneyOK = select.account.isRealMoneyOK(rootState);
      const citizenship = select.account.getUserCitizenship(rootState);
      const denmarkCitizen = isDenmarkCitizen(citizenship || '');

      const isSwcLugasMessageEnabled = select.feature.check(rootState, FeatureFlag.SwcLugasMessage);
      return (
        amlEventType === RegulatorAmlValEventType.ACCOUNT_SUSPENDED ||
        currentlyTakingBreak ||
        isSuspendedRM ||
        depositRestricted ||
        isSwcLugasMessageEnabled ||
        (isSiteUS && !isRealMoneyOK) ||
        (isSiteDK && !isRealMoneyOK && denmarkCitizen)
      );
    },
    [amlEventType, currentlyTakingBreak, isSiteUS, isSuspendedRM, isSiteDK]
  );

  const showPendingCashout = ({ data: { pendingCashouts } }: { data: FirstWizardData }) =>
    !isEmpty(pendingCashouts);

  const depositCashoutMenuSelect = ({
    values: {
      pendingCashouts: { menuSelected }
    }
  }: {
    values: { pendingCashouts: { menuSelected: 'pendingCashout' | 'deposit' } };
  }) => menuSelected === 'pendingCashout';

  const retryDepositCheck = ({ data: { depositResult } }: { data: FirstWizardData }) =>
    !!depositResult?.retry;

  const isRedirectExternalAppContextState = ({ data: { depositResultContext } }: WizardContext) =>
    !!depositResultContext?.appSchemaUrl;

  const doesAccountRequireOccupation = useCallback(
    () => isRealMoneyOK && isSiteES && !occupationInfo?.occupation,
    [isRealMoneyOK, isSiteES, occupationInfo]
  );

  const isDepositCumulative = ({ data: { availableDepositAmount }, values }: WizardContext) => {
    const amount = dollarsToCents(trimWithoutRounding(values.deposit.amount));
    const availableAmount = availableDepositAmount.amt;
    return (
      availableDepositAmount && availableAmount && availableAmount >= 0 && availableAmount <= amount
    );
  };
  const depositEmailVerificationCheck = () =>
    isSiteUSPA && isSWCEmailVerificationEnabled && !emailVerified;

  return {
    accountSuspended,
    checkSWFFlowGuard,
    depositCashoutMenuSelect,
    depositDetailsConfirmCheck,
    depositNoDetails,
    depositResultContextPresent,
    doesAccountRequireOccupation,
    isDepositCumulative,
    isRedirectExternalAppContextState,
    shouldProcessOfferBonus,
    shouldProcessAmountBonus,
    showPendingCashout,
    showWelcomeBonusOffer,
    retryDepositCheck,
    depositMitIdDataPresent,
    hasDepositMitIdData,
    depositEmailVerificationCheck
  };
};

export default useDepositGuardsData;
